import { useState, type ReactNode } from 'react';
import type { CurrentUser } from '../api';
import { t } from '../i18n';
import { Link, usePath } from '../router';

const NAV: Array<{ key: Parameters<typeof t>[0]; to: string }> = [
  { key: 'nav.overview', to: '/' },
  { key: 'nav.inbox', to: '/inbox' },
  { key: 'nav.tasks', to: '/tasks' },
  { key: 'nav.dialogs', to: '/dialogs' },
  { key: 'nav.plans', to: '/plans' },
  { key: 'nav.approvals', to: '/approvals' },
  { key: 'nav.agents', to: '/agents' },
  { key: 'nav.knowledge', to: '/knowledge' },
  { key: 'nav.processes', to: '/processes' },
  { key: 'nav.runs', to: '/runs' },
  { key: 'nav.finance', to: '/finance' },
  { key: 'nav.team', to: '/team' },
  { key: 'nav.audit', to: '/audit' },
];

/**
 * Registry pattern "app shell": persistent left sidebar on desktop, drawer on mobile. The nav
 * badge (registry pattern "nav badge") carries the count of things waiting for the user on that
 * screen — in phase 2 the pending approvals from `GET /me`, refreshed by the screen itself
 * after every decision so the sidebar never lags behind the card.
 */
export function Shell({ user, onLogout, children }: { user: CurrentUser; onLogout: () => void; children: ReactNode }) {
  const path = usePath();
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const isCurrent = (to: string) => (to === '/' ? path === '/' : path === to || path.startsWith(to + '/'));

  return (
    <div className="shell">
      <div className={`backdrop${open ? ' open' : ''}`} onClick={close} aria-hidden="true" />
      <aside className={`sidebar${open ? ' open' : ''}`} aria-label={t('common.menu')}>
        <div className="brand">{t('app.name')}</div>
        <nav>
          {NAV.map((n) => {
            const badge = n.to === '/approvals' ? (user.pendingApprovals ?? 0) : 0;
            return (
              <Link key={n.to} to={n.to} onNavigate={close} aria-current={isCurrent(n.to) ? 'page' : undefined}>
                {t(n.key)}
                {badge > 0 && (
                  <span className="nav-badge" aria-label={t('approvals.badge', { n: badge })}>
                    {badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
        <div className="spacer" />
        <div className="user">
          <Link to="/profile" onNavigate={close} aria-current={isCurrent('/profile') ? 'page' : undefined}>
            {user.displayName}
          </Link>
          <button className="btn secondary small" onClick={onLogout}>{t('auth.logout')}</button>
        </div>
      </aside>
      <div className="main">
        <header className="topbar">
          <button className="btn secondary small" onClick={() => setOpen(true)} aria-expanded={open} aria-controls="sidebar">{t('common.menu')}</button>
          <span className="brand">{t('app.name')}</span>
        </header>
        <main className="content">{children}</main>
      </div>
    </div>
  );
}
